import * as React from 'react';

import { KeyboardReact as Keyboard } from 'react-simple-keyboard';

import 'react-simple-keyboard/build/css/index.css';
import { cn } from '../../../lib/stylingUtils';

const lucideArrowUpSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-up"><path d="m5 12 7-7 7 7"></path><path d="M12 19V5"></path></svg>';
const lucideChevronDownSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down"><path d="m6 9 6 6 6-6"></path></svg>';
const lucideBackspaceSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-delete"><path d="M10 5a2 2 0 0 0-1.344.519l-6.328 5.74a1 1 0 0 0 0 1.481l6.328 5.741A2 2 0 0 0 10 19h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z"/><path d="m12 9 6 6"/><path d="m18 9-6 6"/></svg>';

export type VirtualQwertyKeyboardHandle = {
  setInput: (text: string) => void;
};

export type VirtualKeyboardProps = {
  onChange?: (text: string) => void;
  onHide?: () => void;
};

export const VirtualQwertyKeyboard = React.forwardRef<
  VirtualQwertyKeyboardHandle,
  VirtualKeyboardProps
>(({ onChange, onHide }, ref) => {
  const keyboardRef = React.useRef<VirtualQwertyKeyboardHandle>(null);
  const [isShiftPressed, setIsShiftPressed] = React.useState(false);

  React.useImperativeHandle(
    ref,
    () => ({
      setInput: (text: string) => {
        if (keyboardRef.current) {
          keyboardRef.current?.setInput(text);
        }
      },
    }),
    [],
  );

  const onKeyPress = (button: string) => {
    if (button === '{shift}' || button === '{shiftactivated}') {
      setIsShiftPressed(!isShiftPressed);
    }

    if (button === '{hide}' || button === '{enter}') {
      onHide?.();
    }
  };

  /*
   * Clean class string to remove any empty tokens that would cause DOMTokenList errors
   */
  const cleanClassString = (str: string) =>
    str
      .split(' ')
      .filter((token) => token.length > 0)
      .join(' ');

  return (
    <div className="w-full bg-neutral" onMouseDown={(e) => e.preventDefault()}>
      <Keyboard
        keyboardRef={(r) => {
          keyboardRef.current = r;
        }}
        onChange={onChange}
        onKeyPress={onKeyPress}
        theme={cleanClassString(
          // eslint-disable-next-line tailwindcss/no-custom-classname
          cn(
            '@VirtualQwertyKeyboard hg-theme-default',
            'mx-auto my-5 max-w-[1140px] rounded-none px-30 text-2xl font-medium',
            '[&_.hg-button]:!rounded-[5px]',
            '[&_.hg-button]:!border-none [&_.hg-button]:!shadow-none',
            '[&_.hg-button_>_span]:!flex [&_.hg-button_>_span]:!items-center [&_.hg-button_>_span]:!justify-center',
            '[&_.hg-button-enter]:!text-xl',
            '[&_.hg-row:nth-child(2)]:!justify-end',
            '[&.hg-theme-default]:!bg-neutral',
            "[&_.hg-row:nth-child(3)::after]:!grow-[2] [&_.hg-row:nth-child(3)::after]:content-[''] [&_.hg-row:nth-child(3)::before]:!grow [&_.hg-row:nth-child(3)::before]:content-['']",
          ),
        )}
        layoutName={isShiftPressed ? 'shift' : 'default'}
        layout={{
          default: [
            '1 2 3 4 5 6 7 8 9 0 - + {bksp}',
            'q w e r t y u i o p ( ) \\',
            'a s d f g h j k l ; "',
            '{shift} z x c v b n m , . / ?',
            onHide ? '{hide} {space} {enter}' : '{space} {enter}',
          ],
          shift: [
            '! @ # $ % ^ & * 9 0 - = {bksp}',
            'Q W E R T Y U I O P [ ] \\',
            'A S D F G H J K L : "',
            '{shiftactivated} Z X C V B N M , . / ?',
            onHide ? '{hide} {space} {enter}' : '{space} {enter}',
          ],
        }}
        display={{
          '{shift}': lucideArrowUpSvg,
          '{shiftactivated}': lucideArrowUpSvg,
          '{bksp}': `${lucideBackspaceSvg}`,
          '{hide}': `${lucideChevronDownSvg} Hide keyboard`,
          '{space}': ' ',
          '{enter}': 'Enter',
        }}
        buttonTheme={[
          {
            buttons:
              '1 2 3 4 5 6 7 8 9 0 q w e r t y u i o p a s d f g h j k l z x c v b n m ! @ # $ % ^ & * 9 0 Q W E R T Y U I O P A S D F G H J K L Z X C V B N M - + ( ) \\ ; " , . / ? = [ ] :',
            class: cleanClassString(
              cn('max-w-[78px] !text-white [&]:!bg-white/20'),
            ),
          },
          {
            buttons: '- + ( ) \\ ; " , . / ? = [ ] :',
            class: cleanClassString(cn('!opacity-30')),
          },
          {
            buttons: '{shift}',
            class: cleanClassString(
              cn('max-w-24 !text-white [&]:!bg-white/20'),
            ),
          },
          {
            buttons: '{shiftactivated}',
            class: cleanClassString(cn('max-w-24 !text-white [&]:!bg-primary')),
          },
          {
            buttons: '{hide}',
            class: cleanClassString(
              cn(
                'max-w-fit !bg-transparent px-6 py-2 text-base text-white/75 [&>span]:!flex [&>span]:!items-center [&>span]:!justify-center [&>span]:gap-2',
              ),
            ),
          },
          {
            buttons: '{enter}',
            class: cleanClassString(
              cn('max-w-fit min-w-50 px-16 !text-white [&]:!bg-white/20'),
            ),
          },
          {
            buttons: '{space}',
            class: cleanClassString(
              cn('mr-36 ml-16 max-w-132 !text-white [&]:!bg-white/20'),
            ),
          },
          {
            buttons: '{bksp}',
            class: cleanClassString(
              cn('min-w-32 !text-white [&]:!bg-white/20'),
            ),
          },
          ...[
            '1',
            '2',
            '3',
            '4',
            '5',
            '6',
            '7',
            '8',
            '9',
            '0',
            '!',
            '@',
            '#',
            '$',
            '%',
            '^',
            '&',
            '*',
            '+',
            '(',
            ')',
            ';',
            '=',
            '[',
            ']',
            ':',
          ].map((button) => ({
            buttons: button,
            class: cleanClassString(
              // eslint-disable-next-line tailwindcss/no-custom-classname
              cn(
                'hg-button-with-indicator',
                'relative after:absolute after:top-0.5 after:right-1.5 after:font-mono after:text-base [&]:after:!text-[rgba(255,255,255,0.4)]',
                button === '1' && "after:content-['!']",
                button === '2' && "after:content-['@']",
                button === '3' && "after:content-['#']",
                button === '4' && "after:content-['$']",
                button === '5' && "after:content-['%']",
                button === '6' && "after:content-['^']",
                button === '7' && "after:content-['&']",
                button === '8' && "after:content-['*']",
                button === '!' && "after:content-['1']",
                button === '@' && "after:content-['2']",
                button === '#' && "after:content-['3']",
                button === '$' && "after:content-['4']",
                button === '%' && "after:content-['5']",
                button === '^' && "after:content-['6']",
                button === '&' && "after:content-['7']",
                button === '*' && "after:content-['8']",
                button === '(' && "after:content-['[']",
                button === ')' && "after:content-[']']",
                button === ';' && "after:content-[':']",
                button === '+' && "after:content-['=']",
                button === '=' && "after:content-['+']",
                button === '[' && "after:content-['(']",
                button === ']' && "after:content-[')']",
                button === ':' && "after:content-[';']",
              ),
            ),
          })),
        ]}
      />
    </div>
  );
});
